// Salvaged from ui-sprocket src/theme/createAppTheme.ts — reviewed 2026-09-21: every colour,
// size and shadow literal replaced by a token from `../tokens`; `accessibleText` moved to
// `contrast.ts`; renamed `createSprocketTheme`; `customShadows.z1` alias dropped; tooltip dark
// background now `NEUTRAL.dark.inverse`; secondary-button edge now `ELEVATION.control`.
import { alpha, createTheme } from '@mui/material/styles';
import './augmentation.js';
import { BRAND_ALPHA, CONTRAST, HOVER_ALPHA, NEUTRAL, SEMANTIC, } from '../tokens/color.js';
import { ELEVATION, FOCUS } from '../tokens/elevation.js';
import { MOTION } from '../tokens/motion.js';
import { RADIUS } from '../tokens/radius.js';
import { SIZE, SPACE } from '../tokens/space.js';
import { FONT_FAMILY, rem, TYPE } from '../tokens/type.js';
import { accessibleText } from './contrast.js';
/**
 * Builds the MUI theme for a tenant by merging the shared design language with the
 * tenant's brand palette. This is the single place brand colour enters the UI.
 */
export function createSprocketTheme(brand, mode = 'light') {
    const isLight = mode === 'light';
    const n = NEUTRAL[mode];
    const s = SEMANTIC[mode];
    const shadows = ELEVATION[mode];
    // A dark brand on a dark surface would vanish as a filled button; lift it to the
    // 3:1 non-text minimum. Light mode takes the brand exactly as supplied.
    const primaryMain = isLight
        ? brand.primary
        : accessibleText(brand.primary, n.surface, false, CONTRAST.control);
    const secondaryMain = isLight
        ? brand.secondary
        : accessibleText(brand.secondary, n.surface, false, CONTRAST.control);
    const brandText = accessibleText(primaryMain, n.surface, isLight);
    const brandTint = alpha(primaryMain, BRAND_ALPHA[mode].tint);
    const brandTintSoft = alpha(primaryMain, BRAND_ALPHA[mode].tintSoft);
    return createTheme({
        palette: {
            mode,
            contrastThreshold: CONTRAST.text,
            primary: { main: primaryMain },
            secondary: { main: secondaryMain },
            success: { main: s.success.fg },
            warning: { main: s.warning.fg },
            error: { main: s.error.fg },
            info: { main: s.info.fg },
            background: {
                default: n.canvas,
                paper: n.surface,
                canvas: n.canvas,
                sidebar: n.sidebar,
                subtle: n.subtle,
                inverse: n.inverse,
            },
            text: {
                primary: n.text1,
                secondary: n.text2,
                tertiary: n.text3,
                disabled: n.text3,
            },
            divider: n.hairline,
            hairline: n.hairline,
            hairline2: n.hairline2,
            border: n.border,
            brand: { text: brandText, tint: brandTint, tintSoft: brandTintSoft },
            action: {
                hover: alpha(n.text1, HOVER_ALPHA[mode]),
                selected: brandTintSoft,
                focus: brandTint,
            },
        },
        shape: { borderRadius: RADIUS.md },
        customShadows: shadows,
        /** The type scale from the design language (§2). */
        typography: {
            fontFamily: FONT_FAMILY.sans,
            fontSize: TYPE.body.size,
            h1: typeStyle('display'),
            h2: typeStyle('pageTitle'),
            h3: typeStyle('section'),
            h4: { fontSize: rem(16), lineHeight: 1.35, fontWeight: 600 },
            h5: { fontSize: rem(15), lineHeight: 1.4, fontWeight: 600 },
            h6: typeStyle('cardTitle'),
            subtitle1: { fontSize: rem(14), lineHeight: 1.5, fontWeight: 500 },
            subtitle2: typeStyle('label'),
            body1: typeStyle('body'),
            body2: typeStyle('bodySmall'),
            caption: typeStyle('caption'),
            overline: { ...typeStyle('micro'), textTransform: 'none' },
            button: { ...typeStyle('button'), lineHeight: undefined, textTransform: 'none' },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    /**
                     * Honour `prefers-reduced-motion` globally and in CSS rather than in React
                     * state: it then responds the instant the OS setting changes, and covers
                     * animation we never wrote (MUI ripples, dialog/menu transitions) which a JS
                     * flag would miss. `0.01ms` rather than `0` so transition END events still fire.
                     */
                    '@media (prefers-reduced-motion: reduce)': {
                        '*, *::before, *::after': {
                            animationDuration: '0.01ms !important',
                            animationIterationCount: '1 !important',
                            transitionDuration: '0.01ms !important',
                            scrollBehavior: 'auto !important',
                        },
                    },
                    body: {
                        // Long words (an email address in a narrow card) must wrap rather than push
                        // the layout wide enough to scroll the whole page sideways.
                        overflowWrap: 'anywhere',
                        WebkitFontSmoothing: 'antialiased',
                        MozOsxFontSmoothing: 'grayscale',
                    },
                },
            },
            /**
             * MUI maps `subtitle1`/`subtitle2` to <h6> by default, so every subtitle would
             * silently enter the heading outline. A subtitle is styling, not structure.
             */
            MuiTypography: {
                defaultProps: { variantMapping: { subtitle1: 'p', subtitle2: 'p' } },
            },
            MuiPaper: {
                styleOverrides: {
                    outlined: ({ theme }) => ({ borderColor: theme.palette.hairline }),
                },
            },
            MuiCard: {
                defaultProps: { variant: 'outlined', elevation: 0 },
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: RADIUS.md,
                        borderColor: theme.palette.hairline,
                        boxShadow: theme.customShadows.card,
                        backgroundImage: 'none',
                    }),
                },
            },
            MuiCardContent: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        padding: SPACE.cardPadding.desktop,
                        '&:last-child': { paddingBottom: SPACE.cardPadding.desktop },
                        [theme.breakpoints.down('sm')]: {
                            padding: SPACE.cardPadding.phone,
                            '&:last-child': { paddingBottom: SPACE.cardPadding.phone },
                        },
                    }),
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: ({ theme }) => ({
                        boxShadow: 'none',
                        borderRadius: RADIUS.sm,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        transition: theme.transitions.create(['background-color', 'border-color', 'color', 'box-shadow'], { duration: MOTION.duration.base }),
                        variants: [
                            {
                                // The default outlined button is the neutral "secondary" action. Coloured
                                // outlines (color="error") keep their hue.
                                props: { variant: 'outlined', color: 'primary' },
                                style: {
                                    borderColor: theme.palette.border,
                                    color: theme.palette.text.primary,
                                    backgroundColor: theme.palette.background.paper,
                                    boxShadow: theme.customShadows.control,
                                    '&:hover': {
                                        borderColor: theme.palette.border,
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                },
                            },
                            {
                                props: { variant: 'text', color: 'primary' },
                                style: {
                                    color: theme.palette.text.primary,
                                    '&:hover': { backgroundColor: theme.palette.action.hover },
                                },
                            },
                        ],
                    }),
                    sizeSmall: { height: SIZE.control.small, padding: '0 10px', fontSize: rem(12.5) },
                    sizeMedium: { height: SIZE.control.medium, padding: '0 12px' },
                    sizeLarge: { height: SIZE.control.large, padding: '0 16px', fontSize: rem(14) },
                    startIcon: { marginRight: 6, marginLeft: -2, '& > *:nth-of-type(1)': { fontSize: 16 } },
                    endIcon: { marginLeft: 6, marginRight: -2, '& > *:nth-of-type(1)': { fontSize: 16 } },
                },
            },
            // Comfortable touch targets on touch devices only (WCAG 2.5.5) — desktop stays compact.
            MuiIconButton: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: RADIUS.sm,
                        color: theme.palette.text.secondary,
                        '@media (pointer: coarse)': { minWidth: SIZE.touchTarget, minHeight: SIZE.touchTarget },
                    }),
                },
            },
            MuiCheckbox: {
                defaultProps: { size: 'small' },
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: theme.palette.border,
                        padding: 6,
                        '@media (pointer: coarse)': { minWidth: SIZE.touchTarget, minHeight: SIZE.touchTarget },
                    }),
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: RADIUS.sm,
                        backgroundColor: theme.palette.background.paper,
                        fontSize: rem(TYPE.body.size),
                        transition: theme.transitions.create(['border-color', 'box-shadow'], {
                            duration: MOTION.duration.base,
                        }),
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.border },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.text.tertiary,
                        },
                        '&.Mui-focused': {
                            boxShadow: `0 0 0 ${FOCUS.inputRingWidth}px ${theme.palette.brand.tint}`,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 1,
                        },
                    }),
                    input: { padding: '8.5px 12px' },
                    sizeSmall: { '& .MuiOutlinedInput-input': { padding: '5.5px 10px' } },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: { fontSize: rem(TYPE.body.size) },
                    sizeSmall: { fontSize: rem(TYPE.bodySmall.size) },
                },
            },
            MuiFormHelperText: {
                styleOverrides: { root: { marginLeft: 2, fontSize: rem(TYPE.caption.size) } },
            },
            MuiLink: {
                defaultProps: { underline: 'hover' },
                styleOverrides: {
                    // Contrast-guarded brand colour so links stay AA-legible for any tenant brand.
                    root: { color: brandText, fontWeight: 500 },
                },
            },
            MuiDivider: {
                styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.hairline }) },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: ({ theme }) => ({ backgroundColor: theme.palette.background.subtle }),
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: rem(TYPE.bodySmall.size),
                        padding: `${SPACE.tableCell.y}px ${SPACE.tableCell.x}px`,
                        borderBottomColor: theme.palette.hairline2,
                    }),
                    head: ({ theme }) => ({
                        fontSize: rem(TYPE.caption.size),
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap',
                        borderBottomColor: theme.palette.hairline,
                    }),
                    // Touch devices keep roomier rows so they stay tappable.
                    sizeSmall: {
                        padding: `8px ${SPACE.tableCell.x}px`,
                        '@media (pointer: coarse)': { paddingTop: 12, paddingBottom: 12 },
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '&:last-child td': { borderBottom: 0 },
                        '&.MuiTableRow-hover:hover': { backgroundColor: theme.palette.action.hover },
                        '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: theme.palette.brand.tintSoft,
                        },
                    }),
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: ({ theme }) => ({
                        borderRadius: RADIUS.lg,
                        boxShadow: theme.customShadows.overlay,
                        backgroundImage: 'none',
                        // Full-screen dialogs on a notched phone must clear the system bars.
                        [theme.breakpoints.down('sm')]: {
                            paddingTop: 'env(safe-area-inset-top)',
                            paddingBottom: 'env(safe-area-inset-bottom)',
                        },
                    }),
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontSize: rem(16),
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        padding: '20px 24px 8px',
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: ({ theme }) => ({
                        borderRadius: RADIUS.md,
                        border: `1px solid ${theme.palette.hairline}`,
                        boxShadow: theme.customShadows.overlay,
                        backgroundImage: 'none',
                        marginTop: 4,
                    }),
                    list: { padding: 4 },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: ({ theme }) => ({
                        borderRadius: RADIUS.md,
                        border: `1px solid ${theme.palette.hairline}`,
                        boxShadow: theme.customShadows.overlay,
                        backgroundImage: 'none',
                    }),
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: RADIUS.sm,
                        fontSize: rem(TYPE.label.size),
                        minHeight: SIZE.navRow,
                        padding: '6px 10px',
                        '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: theme.palette.brand.tintSoft,
                        },
                    }),
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: RADIUS.sm,
                        '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: theme.palette.brand.tintSoft,
                        },
                    }),
                },
            },
            MuiListItemIcon: {
                styleOverrides: { root: { minWidth: 32, color: 'inherit' } },
            },
            MuiChip: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: RADIUS.sm,
                        fontWeight: 500,
                        transition: theme.transitions.create(['background-color', 'border-color'], {
                            duration: MOTION.duration.base,
                        }),
                    }),
                    sizeSmall: {
                        height: 22,
                        fontSize: rem(TYPE.micro.size),
                        '& .MuiChip-label': { paddingLeft: 7, paddingRight: 7 },
                    },
                    sizeMedium: { height: SIZE.control.small, fontSize: rem(TYPE.bodySmall.size) },
                    filled: ({ theme }) => ({
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.secondary,
                    }),
                },
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        backgroundColor: theme.palette.background.sidebar,
                        border: `1px solid ${theme.palette.hairline}`,
                        borderRadius: RADIUS.sm + 1,
                        padding: 2,
                        gap: 2,
                    }),
                    grouped: {
                        border: 0,
                        borderRadius: RADIUS.sm - 1,
                        '&:not(:first-of-type), &:not(:last-of-type)': {
                            borderRadius: RADIUS.sm - 1,
                            marginLeft: 0,
                        },
                        '&.Mui-disabled': { border: 0 },
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        border: 0,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: rem(12.5),
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        color: theme.palette.text.secondary,
                        padding: '0 10px',
                        height: 26,
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                        '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            boxShadow: theme.customShadows.card,
                        },
                    }),
                    sizeSmall: { height: 26, padding: '0 10px' },
                    sizeMedium: { height: 30, padding: '0 12px', fontSize: rem(TYPE.bodySmall.size) },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        minHeight: 40,
                        borderBottom: `1px solid ${theme.palette.hairline}`,
                    }),
                    indicator: ({ theme }) => ({ backgroundColor: theme.palette.text.primary, height: 2 }),
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        textTransform: 'none',
                        minHeight: 40,
                        minWidth: 0,
                        padding: '8px 10px',
                        fontSize: rem(TYPE.bodySmall.size),
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        '&.Mui-selected': { color: theme.palette.text.primary },
                    }),
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: ({ theme }) => ({
                        fontSize: rem(TYPE.caption.size),
                        fontWeight: 500,
                        borderRadius: RADIUS.sm,
                        padding: '5px 8px',
                        backgroundColor: theme.palette.background.inverse,
                    }),
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: RADIUS.md,
                        fontSize: rem(TYPE.bodySmall.size),
                        alignItems: 'center',
                    },
                    standard: ({ theme }) => ({ border: `1px solid ${theme.palette.hairline}` }),
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: { fontSize: rem(TYPE.caption.size), fontWeight: 600, letterSpacing: '0.01em' },
                },
            },
            MuiSkeleton: {
                defaultProps: { animation: 'wave' },
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: RADIUS.sm,
                        backgroundColor: alpha(theme.palette.text.primary, isLight ? 0.06 : 0.09),
                    }),
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: theme.palette.hairline2,
                    }),
                    bar: { borderRadius: 3 },
                },
            },
            MuiBottomNavigationAction: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: theme.palette.text.tertiary,
                        '&.Mui-selected': { color: brandText },
                    }),
                    // The retired theme used 10.5px here; the contract's floor is 11.5px (§2).
                    label: {
                        fontSize: rem(TYPE.micro.size),
                        fontWeight: 500,
                        '&.Mui-selected': { fontSize: rem(TYPE.micro.size) },
                    },
                },
            },
            // A visible, consistently-placed focus ring for keyboard users (§4).
            MuiButtonBase: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        '&:focus-visible': {
                            outline: `${FOCUS.ringWidth}px solid ${theme.palette.primary.main}`,
                            outlineOffset: FOCUS.ringOffset,
                        },
                    }),
                },
            },
        },
    });
}
function typeStyle(role) {
    const t = TYPE[role];
    return {
        fontSize: rem(t.size),
        lineHeight: t.lineHeight,
        fontWeight: t.weight,
        letterSpacing: t.tracking === '0' ? 0 : t.tracking,
        ...(t.family === 'mono' ? { fontFamily: FONT_FAMILY.mono } : {}),
    };
}
//# sourceMappingURL=create-theme.js.map